import DetalleQuickviewExporter from "./DetalleQuickviewExporter";
import {Card, CardBody, Divider, Input, Spacer, Button, Select, SelectItem, Textarea} from "@heroui/react";
import { Icon } from "@iconify/react";
import React, { useState, useEffect, useMemo } from "react";
import { format, differenceInDays } from "date-fns";
import {toZonedTime} from "date-fns-tz";
import { CurrencyInput, DateInput, MonthSelect, YearInput, YearQuarterInput, StatusSelect } from "../../components/inputs";
import { apiClientExporter, apiClient } from "../../services/apiClient";
import { toast } from "sonner";
import { Skeleton } from "@heroui/react";
import { calcularDiasQueFaltan } from "../../utils/general/exporterUtil";
import { calcularTimeBar } from "../../utils/general/exporterUtil";

// Hook personalizado para obtener el perfil del usuario exporter
function useExporterProfile() {
    const [userProfile, setUserProfile] = useState(null);
    const [loadingProfile, setLoadingProfile] = useState(false);

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                setLoadingProfile(true);
                const token = localStorage.getItem("exporterAuthToken");
                if (!token) {
                    setUserProfile(null);
                    return;
                }

                const response = await apiClientExporter.get('/user/exporterprofile');
                const data = response.data;

                setUserProfile({
                    name: `${data.first_name || ''} ${data.last_name || ''}`.trim() || 'Exporter User',
                    first_name: data.first_name || '',
                    last_name: data.last_name || '',
                    email: data.email || '',
                    company_name: data.company_name || '',
                    country: data.country || '',
                    exporter_id: data.exporter_id || null,
                    profile_id: data.profile_id ?? null,
                    permissions: Array.isArray(data.permissions) ? data.permissions : []
                });
            } catch (error) {
                console.error('Error al obtener perfil del exportador:', error);
                setUserProfile(null);
            } finally {
                setLoadingProfile(false);
            }
        };

        fetchProfile();
    }, []);

    return { userProfile, loadingProfile };
}

function DetalleMaestraEditar({ maestraData, readOnlyClaims = false, aCargoOptions = [] }) {
    // TODO: Implementar sistema de autenticación para comentarios
    // - Solo el autor del comentario puede editarlo/eliminarlo
    // - Los administradores pueden editar/eliminar cualquier comentario
    // - Conectar con el backend para validar permisos

    // Obtener el perfil del usuario exporter
    const { userProfile, loadingProfile } = useExporterProfile();

    // Estado para controlar el modo de edición
    const [isEditing, setIsEditing] = useState(false);

    // Estado para almacenar el valor original de ofertas_de_cierre
    const [originalOfertasCierre, setOriginalOfertasCierre] = useState(maestraData.ofertas_de_cierre ?? "");

    // Estado para almacenar las opciones de causa
    const [causaOpciones, setCausaOpciones] = useState([]);

    // Estado para ETD desde Box
    const [etdData, setEtdData] = useState(null);
    const [loadingEtd, setLoadingEtd] = useState(true);

    // Estado para la última conversión de moneda
    const [lastConversion, setLastConversion] = useState(null);

    // Cargar última conversión
    const loadLastConversion = async () => {
        if (!maestraData?.id) return;
        try {
            const response = await apiClientExporter.get(`/exporter/currency/last_conversion/${maestraData.id}`);
            setLastConversion(response.data);
        } catch (error) {
            setLastConversion(null);
        }
    };

    useEffect(() => {
        if (maestraData?.id) {
            loadLastConversion();
        }
    }, [maestraData?.id]);

    // Estado para manejar comentarios
    // TODO: En el futuro, estos comentarios se almacenarán en una tabla separada:
    // CREATE TABLE maestra_has_comments (
    //     id SERIAL PRIMARY KEY,
    //     maestra_id UUID NOT NULL,
    //     comment_text TEXT NOT NULL,
    //     author VARCHAR(255) NOT NULL,
    //     user_email VARCHAR(255) NOT NULL,
    //     exporter_id INTEGER,
    //     created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
    //     updated_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
    //     is_quickview_comment BOOLEAN DEFAULT FALSE,
    //     FOREIGN KEY (maestra_id) REFERENCES maestra_recuperos(id) ON DELETE CASCADE
    // );
    // Nota: maestra_id es de tipo UUID porque maestra_recuperos usa UUID como clave primaria
    const [comments, setComments] = useState([
        // Comentario de QuickView - SOLO LECTURA
        {
            id: 'quickview-comment',
            text: maestraData.quickview_claim_survey.generalcomments || "No QuickView comment available",
            author: "QuickView System",
            date: maestraData.fecha_creacion ? new Date(maestraData.fecha_creacion).toLocaleDateString("en-US", {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            }) : new Date().toLocaleDateString("en-US", {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            }),
            time: maestraData.fecha_creacion ? new Date(maestraData.fecha_creacion).toLocaleTimeString("en-US", {
                hour: '2-digit',
                minute: '2-digit'
            }) : new Date().toLocaleTimeString("en-US", {
                hour: '2-digit',
                minute: '2-digit'
            }),
            timestamp: maestraData.fecha_creacion || new Date().toISOString(),
            isQuickViewComment: true, // Marca especial para comentarios de QuickView
            maestraId: maestraData.id // ID de la maestra para futuras referencias
        }
    ]);
    const [newComment, setNewComment] = useState("");

    // Estado para manejar la edición de comentarios
    const [editingCommentId, setEditingCommentId] = useState(null);
    const [editingText, setEditingText] = useState("");

    // Mostrar todos los AI Comments (por defecto solo el más reciente)
    const [showAllAIComments, setShowAllAIComments] = useState(false);

    // Estado para resumen inteligente y comentarios de IA
    const [resumenData, setResumenData] = useState(null);
    const [isGeneratingResumen, setIsGeneratingResumen] = useState(false);

    // Estado para manejar proyecciones personalizadas
    const [projections, setProjections] = useState([]);
    const [availableProjections, setAvailableProjections] = useState([]); // Lista de proyecciones disponibles desde la tabla projections
    const [newProjection, setNewProjection] = useState({
        projection_id: null,
        title: "",
        quickview_percent: "",
        fecha_estimada: "",
        proyeccion_usd: ""
    });
    const [editingProjectionId, setEditingProjectionId] = useState(null);
    const [editingProjection, setEditingProjection] = useState({
        projection_id: null,
        title: "",
        quickview_percent: "",
        fecha_estimada: "",
        proyeccion_usd: ""
    });

    useEffect(() => {
        if (readOnlyClaims) {
            setIsEditing(false);
        }
    }, [readOnlyClaims]);

    // Función para agregar un nuevo comentario
    const handleAddComment = async () => {
        if (readOnlyClaims) return;
        if (newComment.trim()) {
            try {
                // El perfil del usuario se obtiene de antemano usando el hook useExporterProfile
                // Esto asegura que tenemos acceso al nombre real, email e ID del exportador
                const response = await apiClientExporter.post('/maestra/comments', {
                    maestra_id: maestraData.id,
                    comment_text: newComment.trim(),
                    author: userProfile?.name || "Exporter User",
                    user_email: userProfile?.email,
                    exporter_id: userProfile?.exporter_id
                });

                const savedComment = response.data;

                // Crear el objeto del comentario con la respuesta del backend
                const newCommentObj = {
                    id: savedComment.id,
                    text: savedComment.comment_text,
                    author: savedComment.author,
                    date: new Date(savedComment.created_at).toLocaleDateString("en-US", {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                    }),
                    time: new Date(savedComment.created_at).toLocaleTimeString("en-US", {
                        hour: '2-digit',
                        minute: '2-digit'
                    }),
                    timestamp: savedComment.created_at,
                    isQuickViewComment: false,
                    maestraId: savedComment.maestra_id,
                    userEmail: savedComment.user_email,
                    exporterId: savedComment.exporter_id
                };

                // Agregar el comentario al estado local
                setComments(prev => [newCommentObj, ...prev.filter(c => c.id !== 'quickview-comment'), prev.find(c => c.id === 'quickview-comment')]);
                setNewComment(""); // Clear the field

                toast.success("Comment added successfully", {
                    duration: 3000,
                    position: "top-right",
                });
            } catch (error) {
                console.error('Error al agregar comentario:', error);
                toast.error("Error adding comment. Please try again.", {
                    duration: 4000,
                    position: "top-right",
                });
            }
        }
    };

    // Función para iniciar la edición de un comentario
    const handleStartEdit = (comment) => {
        // No permitir editar comentarios de QuickView
        if (comment.isQuickViewComment) {
            console.log("No se puede editar comentarios de QuickView");
            return;
        }

        setEditingCommentId(comment.id);
        setEditingText(comment.text);
    };

    // Función para guardar la edición de un comentario
    const handleSaveEdit = async (commentId) => {
        if (editingText.trim()) {
            try {
                // Actualizar el comentario en el backend
                await apiClientExporter.put(`/maestra/comments/${commentId}`, {
                    comment_text: editingText.trim()
                });

                // Actualizar el estado local
                setComments(prev => prev.map(comment =>
                    comment.id === commentId
                        ? { ...comment, text: editingText.trim() }
                        : comment
                ));

                setEditingCommentId(null);
                setEditingText("");

                toast.success("Comment updated successfully", {
                    duration: 3000,
                    position: "top-right",
                });
            } catch (error) {
                console.error('Error al actualizar comentario:', error);
                toast.error("Error updating comment. Please try again.", {
                    duration: 4000,
                    position: "top-right",
                });
            }
        }
    };

    // Función para cancelar la edición
    const handleCancelEdit = () => {
        setEditingCommentId(null);
        setEditingText("");
    };

    // Función para eliminar un comentario
    const handleDeleteComment = async (commentId) => {
        // No permitir eliminar comentarios de QuickView
        const comment = comments.find(c => c.id === commentId);
        if (comment && comment.isQuickViewComment) {
            console.log("No se puede eliminar comentarios de QuickView");
            return;
        }

        try {
            // Eliminar el comentario del backend
            await apiClientExporter.delete(`/maestra/comments/${commentId}`);

            // Eliminar del estado local
            setComments(prev => prev.filter(comment => comment.id !== commentId));

            toast.success("Comment deleted successfully", {
                duration: 3000,
                position: "top-right",
            });
        } catch (error) {
            console.error('Error al eliminar comentario:', error);
            toast.error("Error deleting comment. Please try again.", {
                duration: 4000,
                position: "top-right",
            });
        }
    };

    // TODO: Implementar validación de autor del comentario
    // Esta función debería verificar si el usuario actual es el autor del comentario
    // Por ahora, simulamos que el usuario actual puede editar/eliminar solo sus comentarios
    const canUserModifyComment = (comment) => {
        // Los comentarios de QuickView NO se pueden modificar
        if (comment.isQuickViewComment) {
            return false;
        }

        // TODO: Conectar con el sistema de autenticación real
        // Verificar si el comentario pertenece al usuario actual
        if (userProfile && comment.userEmail) {
            return comment.userEmail === userProfile.email;
        }

        // Fallback: permitir edición/eliminación para comentarios del usuario
        return true;
    };

    // Función para agregar una nueva proyección
    const handleAddProjection = async () => {
        const proyeccionUsd = parseFloat(newProjection.proyeccion_usd);

        // Validar que se haya seleccionado una proyección
        if (!newProjection.projection_id) {
            toast.error("Please select a projection type");
            return;
        }

        // Validar según el tipo de proyección
        if (proyeccionUsd === 0) {
            // Si proyección USD = 0, solo necesita título
            if (!newProjection.title.trim()) {
                toast.error("Please enter a title for the projection");
                return;
            }
        } else {
            // Si proyección USD > 0, necesita todos los campos
            if (!newProjection.title.trim() || !newProjection.quickview_percent || !newProjection.fecha_estimada) {
                toast.error("Please complete all required fields");
                return;
            }
        }

        try {
            const selectedProj = availableProjections.find(p => p.id === newProjection.projection_id);
            const projectionData = {
                maestra_id: maestraData.id,
                title: newProjection.title.trim() || selectedProj?.title || "",
                author: userProfile?.name || "Exporter User",
                user_email: userProfile?.email,
                exporter_id: userProfile?.exporter_id,
                projection_id: newProjection.projection_id
            };

            // Solo agregar campos si proyección USD > 0
            if (proyeccionUsd > 0) {
                projectionData.quickview_percent = parseFloat(newProjection.quickview_percent);
                projectionData.fecha_estimada = newProjection.fecha_estimada;
                projectionData.proyeccion_usd = proyeccionUsd;
            } else {
                projectionData.proyeccion_usd = 0;
            }

            const response = await apiClientExporter.post('/maestra/projections', projectionData);

            const savedProjection = response.data;

            const newProjectionObj = {
                id: savedProjection.id,
                title: savedProjection.title,
                quickview_percent: savedProjection.quickview_percent,
                fecha_estimada: savedProjection.fecha_estimada,
                proyeccion_usd: savedProjection.proyeccion_usd,
                author: savedProjection.author,
                projection_id: savedProjection.projection_id,
                date: new Date(savedProjection.created_at).toLocaleDateString("en-US", {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                }),
                time: new Date(savedProjection.created_at).toLocaleTimeString("en-US", {
                    hour: '2-digit',
                    minute: '2-digit'
                }),
                timestamp: savedProjection.created_at,
                maestraId: savedProjection.maestra_id,
                userEmail: savedProjection.user_email,
                exporterId: savedProjection.exporter_id
            };

            setProjections(prev => [newProjectionObj, ...prev]);
            setNewProjection({ projection_id: null, title: "", quickview_percent: "", fecha_estimada: "", proyeccion_usd: "" });

            toast.success("Projection added successfully", {
                duration: 3000,
                position: "top-right",
            });
        } catch (error) {
            console.error('Error al agregar proyección:', error);
            const errorMessage = error.response?.data?.message || "Error adding projection. Please try again.";
            toast.error(errorMessage, {
                duration: 4000,
                position: "top-right",
            });
        }
    };

    // Función para iniciar la edición de una proyección
    const handleStartEditProjection = (projection) => {
        setEditingProjectionId(projection.id);
        setEditingProjection({
            projection_id: projection.projection_id,
            title: projection.title,
            quickview_percent: (projection.quickview_percent ?? "").toString(),
            fecha_estimada: projection.fecha_estimada ?? "",
            proyeccion_usd: (projection.proyeccion_usd ?? "").toString()
        });
    };

    // Función para guardar la edición de una proyección
    const handleSaveEditProjection = async (projectionId) => {
        const proyeccionUsd = parseFloat(editingProjection.proyeccion_usd);

        // Validar que se haya seleccionado una proyección
        if (!editingProjection.projection_id) {
            toast.error("Please select a projection type");
            return;
        }

        // Validar según el tipo de proyección
        if (proyeccionUsd === 0) {
            // Si proyección USD = 0, solo necesita título
            if (!editingProjection.title.trim()) {
                toast.error("Please enter a title for the projection");
                return;
            }
        } else {
            // Si proyección USD > 0, necesita todos los campos
            if (!editingProjection.title.trim() || !editingProjection.quickview_percent || !editingProjection.fecha_estimada) {
                toast.error("Please complete all required fields");
                return;
            }
        }

        try {
            const projectionData = {
                title: editingProjection.title.trim(),
                projection_id: editingProjection.projection_id
            };

            // Solo agregar campos si proyección USD > 0
            if (proyeccionUsd > 0) {
                projectionData.quickview_percent = parseFloat(editingProjection.quickview_percent);
                projectionData.fecha_estimada = editingProjection.fecha_estimada;
                projectionData.proyeccion_usd = proyeccionUsd;
            } else {
                projectionData.proyeccion_usd = 0;
            }

            await apiClientExporter.put(`/maestra/projections/${projectionId}`, projectionData);

            setProjections(prev => prev.map(projection =>
                projection.id === projectionId
                    ? {
                        ...projection,
                        title: editingProjection.title.trim(),
                        quickview_percent: proyeccionUsd > 0 ? parseFloat(editingProjection.quickview_percent) : null,
                        fecha_estimada: proyeccionUsd > 0 ? editingProjection.fecha_estimada : null,
                        proyeccion_usd: proyeccionUsd,
                        projection_id: editingProjection.projection_id
                    }
                    : projection
            ));

            setEditingProjectionId(null);
            setEditingProjection({ projection_id: null, title: "", quickview_percent: "", fecha_estimada: "", proyeccion_usd: "" });

            toast.success("Projection updated successfully", {
                duration: 3000,
                position: "top-right",
            });
        } catch (error) {
            console.error('Error al actualizar proyección:', error);
            const errorMessage = error.response?.data?.message || "Error updating projection. Please try again.";
            toast.error(errorMessage, {
                duration: 4000,
                position: "top-right",
            });
        }
    };

    // Función para cancelar la edición de proyección
    const handleCancelEditProjection = () => {
        setEditingProjectionId(null);
        setEditingProjection({ projection_id: null, title: "", quickview_percent: "", fecha_estimada: "", proyeccion_usd: "" });
    };

    // Función para eliminar una proyección
    const handleDeleteProjection = async (projectionId) => {
        try {
            await apiClientExporter.delete(`/maestra/projections/${projectionId}`);
            setProjections(prev => prev.filter(projection => projection.id !== projectionId));

            toast.success("Projection deleted successfully", {
                duration: 3000,
                position: "top-right",
            });
        } catch (error) {
            console.error('Error al eliminar proyección:', error);
            toast.error("Error deleting projection. Please try again.", {
                duration: 4000,
                position: "top-right",
            });
        }
    };

    // Función para verificar si el usuario puede modificar una proyección
    const canUserModifyProjection = (projection) => {
        if (userProfile && projection.userEmail) {
            return projection.userEmail === userProfile.email;
        }
        return true;
    };

    // Estado para almacenar los valores editables
    const [formData, setFormData] = useState({
        rsk_ref: maestraData.rsk_ref ?? "",
        client_name: maestraData.client_name ?? "",
        client_fullname: maestraData.client_fullname ?? "",
        embarcador: maestraData.embarcador ?? "",
        nave: maestraData.nave ?? "",
        bl_awb: maestraData.bl_awb ?? "",
        container: maestraData.container ?? "",
        ref_seguro: maestraData.ref_seguro ?? "",
        voyage_number: maestraData.voyage_number ?? "",
        monto_indemnizado_usd: maestraData.monto_indemnizado_usd ?? "",
        status: maestraData.status ?? "",
        porcentaje_recupero: maestraData.porcentaje_recupero ?? "",
        reclamo_usd: maestraData.reclamo_usd ?? "",
        reclamo_euros: maestraData.reclamo_euros ?? "",
        deducible: maestraData.deducible ?? "",
        claudio: maestraData.claudio ?? "",
        reclamo_a_terceros: maestraData.reclamo_a_terceros ?? "",
        contraparte: maestraData.contraparte ?? "",
        recuperado_usd: maestraData.recuperado_usd ?? "",
        recuperado_euros: maestraData.recuperado_euros ?? "",
        fecha_finiquito: maestraData.fecha_finiquito ?? "",
        time_bar: maestraData.time_bar ?? "",
        duracion_reclamo: maestraData.duracion_reclamo ?? "",
        proyeccion_usd: maestraData.proyeccion_usd ?? "",
        ofertas_de_cierre: maestraData.ofertas_de_cierre ?? "",
        fecha_oferta: maestraData.fecha_oferta ?? "",
        dias_que_faltan: maestraData.dias_que_faltan ?? "",
        comentario: maestraData.comentario ?? "",
        caso_year: maestraData.caso_year ?? "",
        caso_mes: maestraData.caso_mes ?? "",
        finiquito_year: maestraData.finiquito_year ?? "",
        tiempo_al_reclamo: maestraData.tiempo_al_reclamo ?? "",
        finiquito_mes: maestraData.finiquito_mes ?? "",
        especie: maestraData.especie ?? "",
        puerto_origen: maestraData.puerto_origen ?? "",
        puerto_destino: maestraData.puerto_destino ?? "",
        causa: maestraData.causa ?? "",
        fecha_creacion: maestraData.fecha_creacion ?? "",
        fecha_actualizacion: maestraData.fecha_actualizacion ?? "",
        a_cargo: maestraData.a_cargo ?? "",
        directo: maestraData.directo ?? "",
        id: maestraData.id ?? "",
        fecha_arribo: maestraData.fecha_arribo ?? "",
        time_extension: maestraData.time_extension ?? "",
        pais: maestraData.pais ?? "",
        quickview_percent_base: maestraData.quickview_percent_base
            ? (parseFloat(maestraData.quickview_percent_base) * 100).toString()
            : "",
        fecha_estimada: maestraData.fecha_estimada ?? "",
        rsk_fee_percent: maestraData.rsk_fee_percent
            ? (parseFloat(maestraData.rsk_fee_percent) * 100).toString()
            : "",
        rsk_fee_amount: "", // Autocalculated
        eta: maestraData.eta ? format(toZonedTime(new Date(maestraData.eta), "UTC"), "dd-MM-yyyy") : "",
        ata: maestraData.fecha_arribo ? format(toZonedTime(new Date(maestraData.fecha_arribo), "UTC"), "dd-MM-yyyy") : "",
    });

    // Si maestraData cambia, actualizar formData (incluyendo la conversión de quickview_percent_base)
    useEffect(() => {
        setFormData(prev => ({
            ...prev,
            client_fullname: maestraData.client_fullname ?? prev.client_fullname,
            quickview_percent_base: maestraData.quickview_percent_base
                ? (parseFloat(maestraData.quickview_percent_base) * 100).toString()
                : "",
        }));
    }, [maestraData.quickview_percent_base, maestraData.client_fullname]);

    // Cargar las opciones de causa desde el backend
    useEffect(() => {
        const fetchCausaOpciones = async () => {
            try {
                const response = await apiClientExporter.get('/exporter/causa/opciones');
                let opciones = response.data;

                // Agregar el valor actual de causa si existe y no está en las opciones estándar
                if (maestraData.causa && maestraData.causa.trim() !== "" &&
                    !opciones.some(opcion => opcion.value === maestraData.causa)) {
                    opciones = [
                        {
                            id: 'current',
                            label: maestraData.causa,  // ✅ Sin sufijo "(valor actual)"
                            value: maestraData.causa
                        },
                        ...opciones
                    ];
                }

                setCausaOpciones(opciones);
            } catch (error) {
                console.error('Error al cargar las opciones de causa:', error);
                toast.error('Error al cargar las opciones de causa');
            }
        };

        fetchCausaOpciones();
    }, [maestraData.causa]);

    // Cargar ETD desde Box
    useEffect(() => {
        const fetchEtdData = async () => {
            if (!maestraData.rsk_ref) {
                setLoadingEtd(false);
                return;
            }

            try {
                setLoadingEtd(true);
                console.log('Cargando ETD para rsk_ref:', maestraData.rsk_ref);
                const response = await apiClient.get(`/quickview/claimfrombox?rsk_ref=${maestraData.rsk_ref}`);
                console.log('Respuesta ETD:', response.data);
                setEtdData(response.data);
            } catch (error) {
                console.error('Error al cargar ETD desde Box:', error);
                setEtdData(null);
            } finally {
                setLoadingEtd(false);
            }
        };

        fetchEtdData();
    }, [maestraData.rsk_ref]);


    // Función para determinar el color basado en los días que faltan
    const getDiasColor = (diasQueFaltan) => {
        if (diasQueFaltan === "N/A" || diasQueFaltan === "Vencido") {
            return "default"; // Gris para N/A y Vencido
        }

        const dias = parseInt(diasQueFaltan);
        if (isNaN(dias)) return "default";

        if (dias <= 30) {
            return "danger"; // Rojo para 30 días o menos
        } else if (dias <= 60) {
            return "warning"; // Amarillo para 31-60 días
        } else {
            return "success"; // Verde para más de 60 días
        }
    };

    // --- CÁLCULOS AUTOCALCULADOS ---
    useEffect(() => {

        // Calcular días que faltan (usa time_extension o time_bar)
        const diasQueFaltan = calcularDiasQueFaltan(formData.time_extension, formData.fecha_arribo);

        // Calcular claudio
        let claudio = "";
        const deducible = parseFloat(formData.deducible);
        const reclamoUsd = parseFloat(formData.reclamo_usd);
        const montoIndemnizado = parseFloat(formData.monto_indemnizado_usd);
        if (!isNaN(deducible) && deducible === 0) {
            claudio = !isNaN(reclamoUsd) ? reclamoUsd : "";
        } else {
            claudio = !isNaN(montoIndemnizado) ? montoIndemnizado : "";
        }

        // Calcular proyeccion_usd
        let proyeccionUsd = "";
        const quickviewPercent = parseFloat(formData.quickview_percent_base);
        if (!isNaN(reclamoUsd) && !isNaN(quickviewPercent)) {
            proyeccionUsd = (reclamoUsd * quickviewPercent / 100).toFixed(2);
        }

        // Calcular % recupero
        //=SI((P2>0);(AG2/P2);(AG2/S2))
        //si Monto Indemnizado (USD) > 0 ; (Recuperado USD / Monto Indemnizado (USD) ) ; (Recuperado USD / Reclamo USD))
        let porcentajeRecupero = "";
        const recuperadoUsd = parseFloat(formData.recuperado_usd);
        if (!isNaN(montoIndemnizado) && montoIndemnizado > 0 && !isNaN(recuperadoUsd)) {
            porcentajeRecupero = ((recuperadoUsd / montoIndemnizado) * 100).toFixed(2);
        } else if (!isNaN(reclamoUsd) && reclamoUsd > 0 && !isNaN(recuperadoUsd)) {
            porcentajeRecupero = ((recuperadoUsd / reclamoUsd) * 100).toFixed(2);
        }

        // Calcular año y mes de caso (fecha_arribo)
        let casoYear = "";
        let casoMes = "";
        if (formData.fecha_arribo) {
            const fechaArribo = new Date(formData.fecha_arribo);
            casoYear = fechaArribo.getFullYear().toString();
            casoMes = (fechaArribo.getMonth() + 1).toString();
        }

        // Calcular tiempo al reclamo (reclamo_a_terceros - fecha_arribo) / 30
        let tiempoAlReclamo = "";
        if (formData.reclamo_a_terceros && formData.fecha_arribo) {
            const f1 = new Date(formData.reclamo_a_terceros);
            const f2 = new Date(formData.fecha_arribo);
            tiempoAlReclamo = ((f1 - f2) / (1000 * 60 * 60 * 24 * 30)).toFixed(2);
        }

        // Calcular duración reclamo (fecha_finiquito - reclamo_a_terceros) / 30
        let duracionReclamo = "";
        if (formData.fecha_finiquito && formData.reclamo_a_terceros) {
            const f1 = new Date(formData.fecha_finiquito);
            const f2 = new Date(formData.reclamo_a_terceros);
            duracionReclamo = ((f1 - f2) / (1000 * 60 * 60 * 24 * 30)).toFixed(2);
        }

        // Calcular año y mes de finiquito
        let finiquitoYear = "";
        let finiquitoMes = "";
        if (formData.fecha_finiquito) {
            const fechaFiniquito = new Date(formData.fecha_finiquito);
            finiquitoYear = fechaFiniquito.getFullYear().toString();
            finiquitoMes = (fechaFiniquito.getMonth() + 1).toString();
        }

        // Calcular RSK Fee Amount
        let rskFeeAmount = "";
        const rskFeePercent = parseFloat(formData.rsk_fee_percent);
        const isFinalized = formData.status === "Finiquito Firmado" || formData.status === "Cerrado";

        if (isFinalized && !isNaN(recuperadoUsd) && !isNaN(rskFeePercent)) {
            rskFeeAmount = (recuperadoUsd * rskFeePercent / 100).toFixed(2);
        }

        setFormData(prevData => ({
            ...prevData,
            time_bar: calcularTimeBar(formData.fecha_arribo, true),
            dias_que_faltan: diasQueFaltan,
            claudio: claudio,
            proyeccion_usd: proyeccionUsd,
            porcentaje_recupero: porcentajeRecupero,
            caso_year: casoYear,
            caso_mes: casoMes,
            tiempo_al_reclamo: tiempoAlReclamo,
            duracion_reclamo: duracionReclamo,
            finiquito_year: finiquitoYear,
            finiquito_mes: finiquitoMes,
            rsk_fee_amount: rskFeeAmount
        }));
    // eslint-disable-next-line
    }, [formData.deducible, formData.reclamo_usd, formData.monto_indemnizado_usd, formData.quickview_percent_base, formData.recuperado_usd, formData.fecha_arribo, formData.reclamo_a_terceros, formData.fecha_finiquito, formData.time_extension, formData.rsk_fee_percent, formData.status]);

    // Manejador para actualizar los valores de los campos
    const handleInputChange = (field, value) => {
        console.log("field", field);
        console.log("value", value);

        // Lógica especial para ofertas_de_cierre
        if (field === "ofertas_de_cierre") {
            const fechaActual = format(new Date(), "yyyy-MM-dd");

            setFormData((prevData) => {
                const newData = {
                    ...prevData,
                    [field]: value,
                };

                // Si el valor cambió y es diferente del original, actualizar fecha_oferta
                if (value !== originalOfertasCierre) {
                    newData.fecha_oferta = fechaActual;
                } else {
                    // Si vuelve al valor original, restaurar la fecha original
                    newData.fecha_oferta = maestraData.fecha_oferta ?? "";
                }

                return newData;
            });
        } else {
            setFormData((prevData) => {
                const newData = { ...prevData, [field]: value };

                // Lógica de porcentaje RSK Fee por defecto según estado
                if (field === "status") {
                    const currentPercent = prevData.rsk_fee_percent;

                    if (value === "Juicio Chile" || value === "Juicio Perú") {
                        // Si cambia a Juicio -> 20%
                        // Solo si está vacío o si tenía el valor por defecto de Reclamado (15)
                        if (!currentPercent || currentPercent === "15") {
                             newData.rsk_fee_percent = "20";
                        }
                    } else if (value === "Reclamado") {
                        // Si cambia a Reclamado -> 15%
                        // Solo si está vacío o si tenía el valor por defecto de Juicio (20)
                        if (!currentPercent || currentPercent === "20") {
                            newData.rsk_fee_percent = "15";
                        }
                    }
                    // Para otros estados no cambiamos el valor automáticamente
                }

                // Automatización del Status
                if (field === "recuperado_usd") {
                    const recuperadoValue = parseFloat(value) || 0;

                    console.log("Automatización Status - Recuperado USD:", {
                        field,
                        value,
                        recuperadoValue,
                        condition: recuperadoValue === 0
                    });

                    // Si recuperado es 0, cambiar a "Cerrado" siempre
                    if (recuperadoValue === 0) {
                        newData.status = "Cerrado";
                        // No cambiar RSK Fee, se mantiene el que estaba

                        console.log("Cambiando status a 'Cerrado'");
                        toast.success("Status automatically changed to 'Closed'", {
                            duration: 3000,
                            position: "top-right",
                        });
                    }
                }

                return newData;
            });
        }
    };

    const handleDateChange = (field, newValue) => {
        console.log("field", field);
        console.log("value", newValue);
        const formattedDate = newValue
            ? format(new Date(newValue.year, newValue.month - 1, newValue.day), "yyyy-MM-dd")
            : null;

        setFormData((prevData) => {
            const newData = { ...prevData, [field]: formattedDate };

            // Automatización del Status para fecha de finiquito
            if (field === "fecha_finiquito") {
                const recuperadoValue = parseFloat(prevData.recuperado_usd) || 0;

                console.log("Automatización Status - Fecha Finiquito:", {
                    field,
                    formattedDate,
                    recuperadoValue,
                    condition: formattedDate && recuperadoValue > 0
                });

                // Si se establece una fecha de finiquito y recuperado NO es 0, cambiar a "Finiquito Firmado"
                if (formattedDate && recuperadoValue > 0) {
                    newData.status = "Finiquito Firmado";
                    // No cambiar RSK Fee, se mantiene el que estaba

                    console.log("Cambiando status a 'Finiquito Firmado'");
                    toast.success("Status automatically changed to 'Signed Settlement'", {
                        duration: 3000,
                        position: "top-right",
                    });
                }
                // Si se establece una fecha de finiquito y recuperado ES 0, NO cambiar el status
                // (el status ya debería ser "Cerrado" por la regla anterior)
            }

            return newData;
        });
    };

    // Formateador de porcentaje: entero sin decimales, con 1 decimal si aplica
    const formatPercentDisplay = (value) => {
        if (value === null || value === undefined || value === "") return "N/A";
        const num = parseFloat(value);
        if (isNaN(num)) return "N/A";
        const display = Number.isInteger(num) ? num.toString() : num.toFixed(1);
        return `${display}%`;
    };

    // Manejador para guardar los cambios
    const handleSave = async () => {
        try {
            const token = localStorage.getItem("exporterAuthToken");

            // Limpiar datos antes de enviar - convertir strings vacíos a null para campos numéricos
            // Eliminar campos autocalculados del payload
            const {
                claudio,
                proyeccion_usd,
                time_bar,
                porcentaje_recupero,
                caso_year,
                caso_mes,
                tiempo_al_reclamo,
                duracion_reclamo,
                finiquito_year,
                finiquito_mes,
                dias_que_faltan,
                rsk_fee_amount,
                ...rest
            } = formData;

            const cleanedData = {
                ...rest,
                monto_indemnizado_usd: rest.monto_indemnizado_usd === "" ? null : rest.monto_indemnizado_usd,
                deducible: rest.deducible === "" ? null : rest.deducible,
                reclamo_usd: rest.reclamo_usd === "" ? null : rest.reclamo_usd,
                reclamo_euros: rest.reclamo_euros === "" ? null : rest.reclamo_euros,
                recuperado_usd: rest.recuperado_usd === "" ? null : rest.recuperado_usd,
                recuperado_euros: rest.recuperado_euros === "" ? null : rest.recuperado_euros,
                ofertas_de_cierre: rest.ofertas_de_cierre === "" ? null : rest.ofertas_de_cierre,
                id: rest.id === "" ? null : rest.id,
                quickview_percent_base: rest.quickview_percent_base
                    ? (parseFloat(rest.quickview_percent_base) / 100).toFixed(4)
                    : null,
                rsk_fee_percent: rest.rsk_fee_percent
                    ? (parseFloat(rest.rsk_fee_percent) / 100).toFixed(4)
                    : null,
            };

            console.log("Datos a enviar al backend:", cleanedData);

            const response = await apiClientExporter.put('/exporter/maestra/recuperos', cleanedData, {
                headers: {
                    "Authorization": `Bearer ${token}`,
                },
            });

            console.log("Datos guardados exitosamente:", response.data);

            // Actualizar el estado local con los datos devueltos por el servidor
            // Esto asegura que campos calculados en backend (como reclamo_usd convertido) se reflejen
            if (response.data) {
                const rd = response.data;
                setFormData(prev => {
                    // En BD los porcentajes están en decimal (0-1); en la vista siempre 0-100
                    const rskFeePercentView = rd.rsk_fee_percent != null && rd.rsk_fee_percent !== ""
                        ? (parseFloat(rd.rsk_fee_percent) * 100).toString()
                        : prev.rsk_fee_percent;
                    const quickviewPercentView = rd.quickview_percent_base != null && rd.quickview_percent_base !== ""
                        ? (parseFloat(rd.quickview_percent_base) * 100).toString()
                        : prev.quickview_percent_base;
                    return {
                        ...prev,
                        ...rd,
                        quickview_percent_base: quickviewPercentView,
                        rsk_fee_percent: rskFeePercentView,
                        reclamo_a_terceros: rd.reclamo_a_terceros ? rd.reclamo_a_terceros.split('T')[0] : null,
                        fecha_finiquito: rd.fecha_finiquito ? rd.fecha_finiquito.split('T')[0] : null,
                        fecha_arribo: rd.fecha_arribo ? rd.fecha_arribo.split('T')[0] : null,
                        time_extension: rd.time_extension ? rd.time_extension.split('T')[0] : null,
                        fecha_oferta: rd.fecha_oferta ? rd.fecha_oferta.split('T')[0] : null,
                        eta: rd.eta ? rd.eta.split('T')[0] : null,
                        ata: rd.ata ? rd.ata.split('T')[0] : null,
                    };
                });
            }

            // Recargar datos de conversión después de guardar
            loadLastConversion();

            // Recargar datos de conversión después de guardar
            loadLastConversion();

            // Recargar datos de conversión después de guardar
            loadLastConversion();

            toast.success("Data successfully saved", {
                duration: 3000,
                position: "top-right",
            });
            setIsEditing(false);
        } catch (error) {
            console.error("Error al guardar los datos:", error);
            toast.error("Error saving data. Please try again.", {
                duration: 4000,
                position: "top-right",
            });
        }
    };

    // Función para cargar comentarios existentes desde el backend
    const loadExistingComments = async () => {
        try {
            const response = await apiClientExporter.get(`/maestra/${maestraData.id}/comments`);
            const existingComments = response.data.map(comment => ({
                id: comment.id,
                text: comment.comment_text,
                author: comment.author,
                date: new Date(comment.created_at).toLocaleDateString("en-US", {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                }),
                time: new Date(comment.created_at).toLocaleTimeString("en-US", {
                    hour: '2-digit',
                    minute: '2-digit'
                }),
                timestamp: comment.created_at,
                isQuickViewComment: false,
                maestraId: comment.maestra_id,
                userEmail: comment.user_email,
                exporterId: comment.exporter_id
            }));

            // Mantener el comentario de QuickView, los comentarios de IA, y agregar los comentarios existentes
            setComments(prevComments => {
                // Preservar comentario de QuickView
                const quickViewComment = prevComments.find(c => c.isQuickViewComment);

                // Preservar todos los comentarios de IA existentes
                const aiComments = prevComments.filter(c => c.isAIComment);

                // Combinar: QuickView, comentarios de usuario del backend, y comentarios de IA
                return [
                    quickViewComment,
                    ...existingComments,
                    ...aiComments
                ].filter(Boolean);
            });
        } catch (error) {
            console.error('Error al cargar comentarios existentes:', error);
            // Si hay error, mantener solo el comentario de QuickView y los comentarios de IA
            setComments(prevComments => {
                const quickViewComment = prevComments.find(c => c.isQuickViewComment);
                const aiComments = prevComments.filter(c => c.isAIComment);
                return [quickViewComment, ...aiComments].filter(Boolean);
            });
        }
    };

    // Función para cargar proyecciones existentes desde el backend
    const loadExistingProjections = async () => {
        try {
            const response = await apiClientExporter.get(`/maestra/${maestraData.id}/projections`);
            const existingProjections = response.data.map(projection => ({
                id: projection.id,
                title: projection.title,
                quickview_percent: projection.quickview_percent,
                fecha_estimada: projection.fecha_estimada,
                proyeccion_usd: projection.proyeccion_usd,
                author: projection.author,
                projection_id: projection.projection_id,
                date: new Date(projection.created_at).toLocaleDateString("en-US", {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                }),
                time: new Date(projection.created_at).toLocaleTimeString("en-US", {
                    hour: '2-digit',
                    minute: '2-digit'
                }),
                timestamp: projection.created_at,
                maestraId: projection.maestra_id,
                userEmail: projection.user_email,
                exporterId: projection.exporter_id
            }));

            setProjections(existingProjections);
        } catch (error) {
            console.error('Error al cargar proyecciones existentes:', error);
            // Si hay error, mantener el array vacío
        }
    };

    // Cargar comentarios existentes cuando se monta el componente
    useEffect(() => {
        if (maestraData.id && userProfile) {
            loadExistingComments();
        }
    }, [maestraData.id, userProfile]);

    // Función para cargar las proyecciones disponibles desde la tabla projections
    const loadAvailableProjections = async () => {
        try {
            const token = localStorage.getItem("exporterAuthToken");
            const response = await apiClientExporter.get('/exporter/projections/list', {
                headers: {
                    "Authorization": `Bearer ${token}`,
                },
            });
            setAvailableProjections(response.data);
        } catch (error) {
            console.error('Error al cargar proyecciones disponibles:', error);
            toast.error("Error loading available projections");
        }
    };

    // Obtener proyecciones disponibles que aún no están asignadas a este caso
    const getAvailableProjectionsForSelect = useMemo(() => {
        const usedProjectionIds = projections.map(p => p.projection_id).filter(id => id !== null && id !== undefined);
        return availableProjections.filter(proj => !usedProjectionIds.includes(proj.id));
    }, [projections, availableProjections]);

    // Cargar proyecciones existentes cuando se monta el componente
    useEffect(() => {
        if (maestraData.id && userProfile) {
            loadExistingProjections();
            loadAvailableProjections();
        }
    }, [maestraData.id, userProfile]);

    // Manejador para alternar el modo de edición
    const toggleEditMode = () => {
        if (readOnlyClaims) return;
        setIsEditing(!isEditing);
    };

    // Editable "A cargo" solo para permisos admin o jefejudicial; el resto siempre solo lectura
    const canEditACargo = useMemo(() => {
        if (!userProfile || !userProfile.permissions) return false;
        const isAdmin = userProfile.permissions.includes('admin');
        const isJefeJudicial = userProfile.permissions.includes('jefejudicial');
        return isAdmin || isJefeJudicial;
    }, [userProfile]);

    // Opciones para "A cargo": lista recibida + valor actual por si no está en la lista
    const aCargoSelectOptions = useMemo(() => {
        const set = new Set(aCargoOptions || []);
        if (formData.a_cargo && String(formData.a_cargo).trim()) set.add(String(formData.a_cargo).trim());
        return Array.from(set).sort();
    }, [aCargoOptions, formData.a_cargo]);

    const identificationFields = [
        { icon: "lucide:hash", label: "Ref. RSK", fieldName: "rsk_ref", value: formData.rsk_ref, tipo: "readonly" },
        { icon: "lucide:hash", label: "Exporter", fieldName: "embarcador", value: formData.embarcador, tipo: "readonly" },
        { icon: "lucide:user", label: "Client", fieldName: "client_name", value: formData.client_name === "DIRECTO" ? formData.embarcador : (formData.client_fullname || formData.client_name), tipo: "readonly" },
        { icon: "lucide:ship", label: "Vessel", fieldName: "nave", value: formData.nave, tipo: "readonly" },
        { icon: "lucide:hash", label: "BL/AWB", fieldName: "bl_awb", value: formData.bl_awb, tipo: "readonly" },
        { icon: "lucide:container", label: "Container", fieldName: "container", value: formData.container, tipo: "readonly" },
        // { icon: "lucide:hash", label: "ID", fieldName: "id", value: formData.id }, // OCULTO TEMPORALMENTE
        { icon: "lucide:hash", label: "Created", fieldName: "fecha_creacion", value: formData.fecha_creacion, tipo: "fecha" },
        { icon: "lucide:hash", label: "Updated", fieldName: "fecha_actualizacion", value: formData.fecha_actualizacion, tipo: "fecha" },
        { icon: "lucide:hash", label: "Species", fieldName: "especie", value: formData.especie, tipo: "readonly" },
        { icon: "lucide:hash", label: "Directo*", fieldName: "directo", value: formData.directo, tipo: "readonly" },
        { icon: "lucide:hash", label: "Port of Loading", fieldName: "puerto_origen", value: formData.puerto_origen, tipo: "readonly" },
        { icon: "lucide:hash", label: "Port of Destination", fieldName: "puerto_destino", value: formData.puerto_destino, tipo: "readonly" },
        { icon: "lucide:hash", label: "In charge", fieldName: "a_cargo", value: formData.a_cargo, tipo: "a_cargo" },
        { icon: "lucide:ship-wheel", label: "Counterparty", fieldName: "contraparte", value: formData.contraparte, tipo: "readonly" },
        { icon: "lucide:hash", label: "Cause", fieldName: "causa", value: formData.causa, tipo: "readonly" },
        { icon: "lucide:hash", label: "Insurance Ref.", fieldName: "ref_seguro", value: formData.ref_seguro },
        { icon: "lucide:ship", label: "Voyage Number", fieldName: "voyage_number", value: formData.voyage_number },
    ];

    // Campos del grupo de reclamos y recuperos (para el Card especial)
    const reclamoFields = [
        { icon: "lucide:dollar-sign", label: "Reclamo USD", fieldName: "reclamo_usd", value: formData.reclamo_usd, tipo: "moneda", currency: "USD", conversion: lastConversion },
        { icon: "lucide:euro", label: "Reclamo Euros", fieldName: "reclamo_euros", value: formData.reclamo_euros, tipo: "moneda", currency: "EUR" },
        { icon: "lucide:dollar-sign", label: "Recuperado USD", fieldName: "recuperado_usd", value: formData.recuperado_usd, tipo: "moneda", currency: "USD" },
        { icon: "lucide:euro", label: "Recuperado Euros", fieldName: "recuperado_euros", value: formData.recuperado_euros, tipo: "moneda", currency: "EUR" },
        { icon: "lucide:hash", label: "Fecha Finiquito", fieldName: "fecha_finiquito", value: formData.fecha_finiquito, tipo: "fecha" },
        { icon: "lucide:percent", label: "% Recupero (auto)", fieldName: "porcentaje_recupero", value: formData.porcentaje_recupero, tipo: "readonly" },
        { icon: "lucide:percent", label: "RSK Fee %", fieldName: "rsk_fee_percent", value: formData.rsk_fee_percent, tipo: "percent", readonly: (!isEditing || formData.status === "Cerrado") },
        { icon: "lucide:dollar-sign", label: "RSK Fee $ (auto)", fieldName: "rsk_fee_amount", value: formData.rsk_fee_amount, tipo: "moneda", currency: "USD", readonly: true },
    ];

    const claimFields = [
        // Claim - Primera sección
        { icon: "lucide:calendar", label: "Reclamo a Terceros", fieldName: "reclamo_a_terceros", value: formData.reclamo_a_terceros, tipo: "fecha" },
        { icon: "lucide:hash", label: "Time bar (auto)", fieldName: "time_bar", value: formData.time_bar, tipo: "fecha" },
        { icon: "lucide:hash", label: "Time Extension", fieldName: "time_extension", value: formData.time_extension, tipo: "fecha" },
        { icon: "lucide:hash", label: "Días que faltan (auto)", fieldName: "dias_que_faltan", value: formData.dias_que_faltan, tipo: "readonly" },

        // Montos y reclamos
        { icon: "lucide:dollar-sign", label: "Monto Indemnizado USD", fieldName: "monto_indemnizado_usd", value: formData.monto_indemnizado_usd, tipo: "moneda", currency: "USD" },
        { icon: "lucide:dollar-sign", label: "Deducible", fieldName: "deducible", value: formData.deducible, tipo: "moneda", currency: "USD" },

        // Ofertas y finiquito - Reorganizados según el nuevo orden
        { icon: "lucide:dollar-sign", label: "Ofertas de Cierre", fieldName: "ofertas_de_cierre", value: formData.ofertas_de_cierre, tipo: "moneda", currency: "USD" },
        { icon: "lucide:hash", label: "Fecha Oferta", fieldName: "fecha_oferta", value: formData.fecha_oferta, tipo: "fecha" },
        { icon: "lucide:calendar", label: "ETD (Box)", fieldName: "etd", value: etdData?.[0]?.etd ? format(toZonedTime(new Date(etdData[0].etd), "UTC"), "dd-MM-yyyy") : "", tipo: "fecha", readonly: true, loading: loadingEtd },
        { icon: "lucide:calendar", label: "ETA", fieldName: "eta", value: formData.eta, tipo: "fecha" },
        { icon: "lucide:calendar", label: "ATA", fieldName: "ata", value: formData.ata, tipo: "fecha" },

        // Status siempre al final
        { icon: "lucide:hash", label: "Status", fieldName: "status", value: formData.status, tipo: "status" },
    ];

    const projectionFields = [
        // Campo oculto - ahora integrado en la sección QuickView
    ];

    const otherFields = [
        { icon: "lucide:calendar-fold", label: "Año Caso (auto)", fieldName: "caso_year", value: formData.caso_year, tipo: "readonly" },
        { icon: "lucide:calendar-1", label: "Mes Caso (auto)", fieldName: "caso_mes", value: formData.caso_mes, tipo: "readonly" },
        { icon: "lucide:calendar-fold", label: "Año Finiquito (auto)", fieldName: "finiquito_year", value: formData.finiquito_year, tipo: "readonly" },
        { icon: "lucide:calendar-1", label: "Mes Finiquito (auto)", fieldName: "finiquito_mes", value: formData.finiquito_mes, tipo: "readonly" },
        { icon: "lucide:hash", label: "Tiempo al Reclamo (auto)", fieldName: "tiempo_al_reclamo", value: formData.tiempo_al_reclamo, tipo: "readonly" },
        { icon: "lucide:hash", label: "Duración Reclamo (auto)", fieldName: "duracion_reclamo", value: formData.duracion_reclamo, tipo: "readonly" },
        // { icon: "lucide:hash", label: "Fecha Arribo", fieldName: "fecha_arribo", value: formData.fecha_arribo, tipo: "fecha" },
        { icon: "lucide:flag", label: "País", fieldName: "pais", value: formData.pais },
    ];

    const commentFields = [
        { icon: "lucide:message-circle", label: "Comentario", fieldName: "comentario", value: formData.comentario },
    ];

    // Función para renderizar el campo apropiado según su tipo
    const renderField = (field, index) => {
        const commonProps = {
            label: field.label,
            value: field.value,
            onChange: (value) => handleInputChange(field.fieldName, value),
            isReadOnly: field.tipo === "fecha" ? !isEditing : !isEditing,
            icon: field.icon,
        };

        // Campo especial: time_bar (auto) - solo lectura, sin calendario
        if (field.fieldName === "time_bar") {
            return (
                <div key={index} className="w-full">
                    <Input
                        label={field.label}
                        value={field.value}
                        isReadOnly={true}
                        startContent={
                            <Icon
                                icon={field.icon}
                                className="text-default-400 pointer-events-none flex-shrink-0"
                            />
                        }
                        classNames={{
                            label: "text-sm font-medium",
                            input: "text-sm",
                        }}
                    />
                </div>
            );
        }

        // Campo especial: etd - solo lectura, formato fecha, con skeleton loading
        if (field.fieldName === "etd") {
            return (
                <div key={index} className="w-full">
                    <Input
                        label={field.label}
                        value={field.value}
                        isReadOnly={true}
                        startContent={
                            <Icon
                                icon={field.icon}
                                className="text-default-400 pointer-events-none flex-shrink-0"
                            />
                        }
                        classNames={{
                            label: "text-sm font-medium",
                            input: "text-sm",
                        }}
                        placeholder={field.loading ? "" : "No hay datos ETD disponibles"}
                    />
                    {field.loading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-background/80">
                            <Skeleton className="h-4 w-24 rounded" />
                        </div>
                    )}
                </div>
            );
        }

        // Campo especial: eta y ata - solo lectura, formato fecha
        if (field.fieldName === "eta" || field.fieldName === "ata") {
            return (
                <div key={index} className="w-full">
                    <Input
                        label={field.label}
                        value={field.value}
                        isReadOnly={true}
                        startContent={
                            <Icon
                                icon={field.icon}
                                className="text-default-400 pointer-events-none flex-shrink-0"
                            />
                        }
                        classNames={{
                            label: "text-sm font-medium",
                            input: "text-sm",
                        }}
                    />
                </div>
            );
        }

        // Campo especial: time_extension - editable y con botón para limpiar
        if (field.fieldName === "time_extension") {
            return (
                <div key={index} className="w-full flex items-end gap-2">
                    <DateInput
                        {...commonProps}
                        onChange={(newValue) => handleDateChange(field.fieldName, newValue)}
                        isReadOnly={!isEditing}
                    />
                    {isEditing && (
                        <Button
                            size="sm"
                            isIconOnly
                            color="default"
                            variant="light"
                            className="rounded-full border border-gray-300 shadow-none hover:bg-gray-100 flex items-center justify-center"
                            onPress={() => handleInputChange(field.fieldName, "")}
                            title="Clean"
                        >
                            <Icon icon="lucide:trash-2" className="text-lg flex items-center justify-center" />
                        </Button>
                    )}
                </div>
            );
        }

        // Campo especial: fecha_oferta - editable y con botón para limpiar
        if (field.fieldName === "fecha_oferta") {
            return (
                <div key={index} className="w-full flex items-end gap-2">
                    <DateInput
                        {...commonProps}
                        onChange={(newValue) => handleDateChange(field.fieldName, newValue)}
                        isReadOnly={!isEditing}
                    />
                    {isEditing && (
                        <Button
                            size="sm"
                            isIconOnly
                            color="default"
                            variant="light"
                            className="rounded-full border border-gray-300 shadow-none hover:bg-gray-100 flex items-center justify-center"
                            onPress={() => handleInputChange(field.fieldName, "")}
                            title="Clean"
                        >
                            <Icon icon="lucide:trash-2" className="text-lg flex items-center justify-center" />
                        </Button>
                    )}
                </div>
            );
        }

        switch (field.tipo) {
            case "moneda":
                // Lógica especial para el campo recuperado_usd
                if (field.fieldName === "recuperado_usd") {
                    return (
                        <div key={index} className="w-full">
                            <CurrencyInput
                                {...commonProps}
                                currency={field.currency || "USD"}
                                placeholder={!field.value || field.value === "" ? "En proceso" : undefined}
                            />
                        </div>
                    );
                }
                // Mostrar datos de conversión si existen para reclamo_usd
                if (field.fieldName === "reclamo_usd" && field.conversion) {
                    const conv = field.conversion;
                    const convDate = new Date(conv.conversion_date).toLocaleDateString();
                    return (
                        <div key={index} className="w-full">
                            <CurrencyInput
                                {...commonProps}
                                currency={field.currency || "USD"}
                            />
                            <div className="text-xs text-blue-600 mt-1 flex flex-col gap-1 bg-blue-50 p-2 rounded border border-blue-100">
                                <div className="flex items-center gap-1 font-medium">
                                    <Icon icon="lucide:arrow-right-left" width={12} />
                                    <span>Automated Conversion:</span>
                                </div>
                                <span>
                                    EUR {new Intl.NumberFormat('de-DE').format(conv.amount_from)}
                                    {' '}on {convDate}
                                </span>
                                <a
                                    href={conv.api_source}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-700 underline truncate hover:text-blue-900 flex items-center gap-1 mt-1"
                                    title="View source API"
                                >
                                    <Icon icon="lucide:external-link" width={10} />
                                    Source: Frankfurter API
                                </a>
                            </div>
                        </div>
                    );
                }
                // Lógica especial para campos de moneda que deben ser siempre readonly (ej. RSK Fee (auto), proyeccion_usd)
                if (field.readonly === true || field.fieldName === "proyeccion_usd") {
                    return (
                        <div key={index} className="w-full">
                            <CurrencyInput
                                label={field.label}
                                value={field.value}
                                onChange={() => {}}
                                isReadOnly={true}
                                icon={field.icon}
                                currency={field.currency || "USD"}
                            />
                        </div>
                    );
                }
                return (
                    <div key={index} className="w-full">
                        <CurrencyInput
                            {...commonProps}
                            currency={field.currency || "USD"}
                        />
                    </div>
                );
            case "fecha":
                return (
                    <div key={index} className="w-full">
                        <DateInput
                            {...commonProps}
                            onChange={(newValue) => handleDateChange(field.fieldName, newValue)}
                            isReadOnly={!isEditing}
                        />
                    </div>
                );
            case "mes":
                return (
                    <div key={index} className="w-full">
                        <MonthSelect
                            {...commonProps}
                        />
                    </div>
                );
            case "año":
                return (
                    <div key={index} className="w-full">
                        <YearInput
                            {...commonProps}
                        />
                    </div>
                );
            case "yearquarter":
                // Lógica especial para el campo fecha_estimada - color naranja cuando no tiene valor
                if (field.fieldName === "fecha_estimada") {
                    const hasValue = field.value && field.value.trim() !== "";
                    return (
                        <div key={index} className="w-full">
                            <YearQuarterInput
                                {...commonProps}
                                classNames={{
                                    label: "text-sm font-medium",
                                    input: hasValue ? "text-sm cursor-pointer" : "text-sm cursor-pointer bg-orange-100 text-orange-700",
                                    inputWrapper: hasValue ? "" : "bg-orange-100 border-orange-300",
                                }}
                            />
                        </div>
                    );
                }
                return (
                    <div key={index} className="w-full">
                        <YearQuarterInput
                            {...commonProps}
                        />
                    </div>
                );
            case "status":
                return (
                    <div key={index} className="w-full">
                        <StatusSelect
                            {...commonProps}
                            classNames={{
                                label: "text-sm font-medium text-blue-700",
                                trigger: "text-sm bg-blue-100 border-blue-300 text-blue-800 font-medium shadow-sm",
                                value: "text-blue-800 font-medium",
                                input: "text-blue-800 bg-blue-50",
                            }}
                        />
                    </div>
                );
            case "causa":
                return (
                    <div key={index} className="w-full">
                        <Select
                            label="Causa"
                            selectedKeys={formData.causa ? [formData.causa] : []}
                            onSelectionChange={(keys) => {
                                const selectedKey = Array.from(keys)[0];
                                console.log("Causa seleccionada:", selectedKey);
                                handleInputChange("causa", selectedKey || "");
                            }}
                            isDisabled={!isEditing}
                            placeholder="Seleccionar causa"
                            classNames={{
                                label: "text-sm font-medium",
                                trigger: "text-sm",
                            }}
                        >
                            {/* Mostrar todas las opciones disponibles (incluyendo el valor actual) */}
                            {causaOpciones.map((causa) => (
                                <SelectItem key={causa.value} value={causa.value}>
                                    {causa.label}
                                </SelectItem>
                            ))}
                        </Select>
                    </div>
                );
            case "percent":
                return (
                    <div key={index} className="w-full">
                        <Input
                            {...commonProps}
                            type="number"
                            // Usar el isReadOnly pasado en commonProps (que viene de !isEditing)
                            // O el readonly específico del campo si existe
                            isReadOnly={field.readonly !== undefined ? field.readonly : !isEditing}
                            onChange={(e) => handleInputChange(field.fieldName, e.target.value)}
                            endContent={
                                <div className="pointer-events-none flex items-center">
                                    <span className="text-default-400 text-small">%</span>
                                </div>
                            }
                            startContent={
                                <Icon
                                    icon={field.icon}
                                    className="text-default-400 pointer-events-none flex-shrink-0"
                                />
                            }
                            classNames={{
                                label: "text-sm font-medium",
                                input: "text-sm",
                            }}
                        />
                    </div>
                );
            case "a_cargo":
                // A cargo: editable solo para jefejudicial o admin; lista desplegable
                if (canEditACargo && isEditing && aCargoSelectOptions.length > 0) {
                    return (
                        <div key={index} className="w-full">
                            <Select
                                label={field.label}
                                selectedKeys={field.value ? [String(field.value)] : []}
                                onSelectionChange={(keys) => {
                                    const selected = Array.from(keys)[0];
                                    handleInputChange("a_cargo", selected || "");
                                }}
                                placeholder="Seleccionar usuario"
                                startContent={
                                    <Icon
                                        icon={field.icon}
                                        className="text-default-400 pointer-events-none flex-shrink-0"
                                    />
                                }
                                classNames={{
                                    label: "text-sm font-medium",
                                    trigger: "text-sm",
                                }}
                            >
                                {aCargoSelectOptions.map((opt) => (
                                    <SelectItem key={opt} className="text-sm">
                                        {opt}
                                    </SelectItem>
                                ))}
                            </Select>
                        </div>
                    );
                }
                return (
                    <div key={index} className="w-full">
                        <Input
                            label={field.label}
                            value={field.value ?? ""}
                            isReadOnly={true}
                            startContent={
                                <Icon
                                    icon={field.icon}
                                    className="text-default-400 pointer-events-none flex-shrink-0"
                                />
                            }
                            classNames={{
                                label: "text-sm font-medium",
                                input: "text-sm",
                            }}
                        />
                    </div>
                );
            case "readonly":
                // Lógica especial para el campo dias_que_faltan
                if (field.fieldName === "dias_que_faltan") {
                    const color = getDiasColor(field.value);
                    return (
                        <div key={index} className="w-full">
                            <Input
                                {...commonProps}
                                isReadOnly={true}
                                color={color}
                                startContent={
                                    <Icon
                                        icon={field.icon}
                                        className="text-default-400 pointer-events-none flex-shrink-0"
                                    />
                                }
                                classNames={{
                                    label: "text-sm font-medium",
                                    input: "text-sm",
                                }}
                            />
                        </div>
                    );
                }
                return (
                    <div key={index} className="w-full">
                        <Input
                            {...commonProps}
                            isReadOnly={true}
                            startContent={
                                <Icon
                                    icon={field.icon}
                                    className="text-default-400 pointer-events-none flex-shrink-0"
                                />
                            }
                            classNames={{
                                label: "text-sm font-medium",
                                input: "text-sm",
                            }}
                        />
                    </div>
                );
            default:
                return (
                    <div key={index} className="w-full">
                        <Input
                            {...commonProps}
                            onChange={(e) => handleInputChange(field.fieldName, e.target.value)}
                            startContent={
                                <Icon
                                    icon={field.icon}
                                    className="text-default-400 pointer-events-none flex-shrink-0"
                                />
                            }
                            classNames={{
                                label: "text-sm font-medium",
                                input: "text-sm",
                            }}
                        />
                    </div>
                );
        }
    };

    return (
        <div>
            <div className="max-w-full">
                <Card className="w-full">
                    <CardBody>
                        <h2 className="text-2xl font-bold">Identification (Read Only)</h2>
                        <Divider className="my-4" />
                        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                            {identificationFields.map((field, index) => renderField(field, index))}
                        </div>
                    </CardBody>
                </Card>
            </div>

            <Spacer />

            <div className="max-w-full">
                <Card className="w-full">
                    <CardBody>
                        <h2 className="text-2xl font-bold">Claim</h2>
                        <Divider className="my-4" />

                        {/* Primera parte de Claim */}
                        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
                            {claimFields.map((field, index) => renderField(field, index))}
                        </div>

                        {/* Grupo especial de Reclamos y Recuperos integrado */}
                        <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50 mb-4">
                            <h3 className="text-lg font-semibold text-blue-800 mb-3 flex items-center gap-2">
                                <Icon icon="lucide:dollar-sign" className="text-blue-600" />
                                Claims and Recoveries
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
                                {reclamoFields.map((field, index) => renderField(field, index))}
                            </div>
                        </div>
                    </CardBody>
                </Card>
            </div>

            <Spacer />

            <div className="max-w-full">
                <Card className="w-full">
                    <CardBody>
                        <h2 className="text-2xl font-bold">Projection (Budget)</h2>
                        <Divider className="my-4" />
                        {/* Sección de QuickView - SOLO LECTURA */}
                        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                            <div className="flex items-center gap-2 mb-3">
                                {/* <Icon icon="mdi:robot-outline" className="text-green-600" width={20} height={20} /> */}
                                <span className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded-full font-medium">
                                    QuickView
                                </span>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">

                                <div className="space-y-1">
                                    <label className="text-xs text-green-600 font-medium flex items-center justify-center gap-1">
                                        % Proyectado - Lower Bound
                                        <Icon icon="lucide:arrow-down" className="text-green-500" width={12} height={12} />
                                    </label>
                                    <Input
                                        value={`${maestraData?.quickview_claim_survey?.lowerbound || "N/A"}%`}
                                        isReadOnly={true}
                                        size="sm"
                                        classNames={{
                                            input: "text-center text-sm font-semibold text-green-700",
                                            inputWrapper: "bg-green-50 border-green-200 h-8"
                                        }}
                                    />
                                </div>

                                <div className="space-y-1">
                                    <label className="text-xs text-green-600 font-medium flex items-center justify-center gap-1">
                                        % Proyectado - Upper Bound
                                        <Icon icon="lucide:arrow-up" className="text-green-500" width={12} height={12} />
                                    </label>
                                    <Input
                                        value={`${maestraData?.quickview_claim_survey?.upperbound || "N/A"}%`}
                                        isReadOnly={true}
                                        size="sm"
                                        classNames={{
                                            input: "text-center text-sm font-semibold text-green-700",
                                            inputWrapper: "bg-green-50 border-green-200 h-8"
                                        }}
                                    />
                                </div>

                                <div className="space-y-1">
                                    <label className="text-xs text-green-600 font-medium flex items-center gap-1">
                                        Proyección USD - Lower
                                        <Icon icon="lucide:arrow-down" className="text-green-500" width={12} height={12} />
                                    </label>
                                    <Input
                                        value={`$${new Intl.NumberFormat('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format((maestraData?.quickview_claim_survey?.lowerbound || 0) * (maestraData?.reclamo_usd || 0) / 100)}`}
                                        isReadOnly={true}
                                        size="sm"
                                        classNames={{
                                            input: "text-center text-sm font-semibold text-green-700",
                                            inputWrapper: "bg-green-50 border-green-200 h-8"
                                        }}
                                    />
                                </div>

                                <div className="space-y-1">
                                    <label className="text-xs text-green-600 font-medium flex items-center gap-1">
                                        Proyección USD - Upper
                                        <Icon icon="lucide:arrow-up" className="text-green-500" width={12} height={12} />
                                    </label>
                                    <Input
                                        value={`$${new Intl.NumberFormat('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format((maestraData?.quickview_claim_survey?.upperbound || 0) * (maestraData?.reclamo_usd || 0) / 100)}`}
                                        isReadOnly={true}
                                        size="sm"
                                        classNames={{
                                            input: "text-center text-sm font-semibold text-green-700",
                                            inputWrapper: "bg-green-50 border-green-200 h-8"
                                        }}
                                    />
                                </div>

                                <div className="space-y-1">
                                    <label className="text-xs text-green-600 font-medium flex items-center justify-center gap-1">
                                        Fecha estimada de recupero
                                        <Icon icon="lucide:calendar" className="text-green-500" width={12} height={12} />
                                    </label>
                                    <YearQuarterInput
                                        label=""
                                        value={formData.fecha_estimada}
                                        onChange={(value) => handleInputChange("fecha_estimada", value)}
                                        isReadOnly={!isEditing}
                                        icon="lucide:calendar"
                                        classNames={{
                                            input: "text-center text-sm h-8",
                                            inputWrapper: "bg-green-50 border-green-200 h-8 min-h-8",
                                            label: "hidden"
                                        }}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                            {projectionFields.map((field, index) => renderField(field, index))}
                        </div>

                        <Divider className="my-4" />

                        {/* Proyecciones Personalizadas */}
                        <div className="mb-4">
                            <h3 className="text-lg font-semibold mb-1">Custom Projections</h3>

                            {/* Campo para agregar nueva proyección */}
                            <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-4">
                                <Select
                                    placeholder="Select Projection Type"
                                    selectedKeys={newProjection.projection_id ? [newProjection.projection_id.toString()] : []}
                                    onSelectionChange={(keys) => {
                                        const selected = Array.from(keys)[0];
                                        if (selected) {
                                            const selectedProj = availableProjections.find(p => p.id === parseInt(selected));
                                            setNewProjection(prev => ({
                                                ...prev,
                                                projection_id: parseInt(selected),
                                                title: selectedProj?.title || ""
                                            }));
                                        } else {
                                            setNewProjection(prev => ({
                                                ...prev,
                                                projection_id: null,
                                                title: ""
                                            }));
                                        }
                                    }}
                                    className="md:col-span-1"
                                    isDisabled={!isEditing}
                                    size="sm"
                                    classNames={{
                                        trigger: "h-8 min-h-8",
                                        value: "text-sm"
                                    }}
                                >
                                    {getAvailableProjectionsForSelect.map((proj) => (
                                        <SelectItem key={proj.id.toString()} value={proj.id.toString()}>
                                            {proj.title}
                                        </SelectItem>
                                    ))}
                                </Select>
                                {getAvailableProjectionsForSelect.length === 0 && (
                                    <div className="md:col-span-1 text-xs text-gray-500 flex items-center">
                                        All projection types are already assigned
                                    </div>
                                )}
                                <Input
                                    placeholder="% Proyectado"
                                    value={newProjection.quickview_percent}
                                    onChange={(e) => {
                                        const percentStr = e.target.value;
                                        const percentNum = parseFloat(percentStr);
                                        const reclamoUsd = parseFloat(formData.reclamo_usd);

                                        setNewProjection(prev => {
                                            const newState = { ...prev, quickview_percent: percentStr };

                                            if (!isNaN(percentNum) && percentNum >= 0 && !isNaN(reclamoUsd) && reclamoUsd >= 0) {
                                                const calc = (reclamoUsd * percentNum) / 100;
                                                newState.proyeccion_usd = calc.toFixed(2);
                                                if (percentNum === 0) {
                                                    newState.fecha_estimada = "";
                                                }
                                            } else {
                                                newState.proyeccion_usd = "";
                                            }

                                            return newState;
                                        });
                                    }}
                                    className="md:col-span-1"
                                    isDisabled={!isEditing}
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="100"
                                    size="sm"
                                    startContent={
                                        <Icon
                                            icon="lucide:percent"
                                            className="text-default-400 pointer-events-none flex-shrink-0"
                                        />
                                    }
                                    classNames={{
                                        input: "text-sm h-8",
                                        inputWrapper: "h-8 min-h-8"
                                    }}
                                />
                                <Input
                                    placeholder="Proyección USD"
                                    value={newProjection.proyeccion_usd}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setNewProjection(prev => {
                                            const newState = { ...prev, proyeccion_usd: value };

                                            // Si proyección USD = 0, limpiar otros campos
                                            if (parseFloat(value) === 0) {
                                                newState.quickview_percent = "";
                                                newState.fecha_estimada = "";
                                            }

                                            return newState;
                                        });
                                    }}
                                    className="md:col-span-1"
                                    isDisabled={!isEditing}
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    size="sm"
                                    startContent={
                                        <Icon
                                            icon="lucide:dollar-sign"
                                            className="text-default-400 pointer-events-none flex-shrink-0"
                                        />
                                    }
                                    classNames={{
                                        input: "text-sm h-8",
                                        inputWrapper: "h-8 min-h-8"
                                    }}
                                />
                                <div className="space-y-1">
                                    {/* <label className="text-xs text-green-600 font-medium flex items-center gap-1">
                                        Fecha estimada de recupero
                                        <Icon icon="lucide:calendar" className="text-green-500" width={12} height={12} />
                                    </label> */}
                                    <YearQuarterInput
                                        label=""
                                        value={newProjection.fecha_estimada}
                                        onChange={(value) => setNewProjection(prev => ({ ...prev, fecha_estimada: value }))}
                                        isReadOnly={!isEditing || parseFloat(newProjection.proyeccion_usd) === 0}
                                        icon="lucide:calendar"
                                        size="sm"
                                        classNames={{
                                            label: "hidden",
                                            inputWrapper: "h-8 min-h-8",
                                            trigger: "h-8 min-h-8"
                                        }}
                                    />
                                </div>
                                <Button
                                    color="primary"
                                    onPress={handleAddProjection}
                                    isDisabled={
                                        !isEditing ||
                                        !newProjection.projection_id ||
                                        !newProjection.title.trim() ||
                                        (parseFloat(newProjection.proyeccion_usd) > 0 && (!newProjection.quickview_percent || !newProjection.fecha_estimada))
                                    }
                                    startContent={<Icon icon="lucide:plus" />}
                                    className="md:col-span-1 h-8 min-h-8"
                                    size="sm"
                                >
                                    Agregar
                                </Button>
                            </div>

                            {/* Lista de proyecciones */}
                            <div className="space-y-3">
                                {projections.length === 0 ? (
                                    <div className="flex items-center justify-center gap-2 py-0 text-gray-500">
                                        <Icon icon="lucide:trending-up" className="text-2xl text-gray-300" />
                                        <p className="m-0">No custom projections. Add the first projection above.</p>
                                    </div>
                                ) : (
                                    projections.map((projection) => (
                                        <div
                                            key={projection.id}
                                            className="border border-gray-200 rounded-lg p-3 bg-gray-50"
                                        >
                                            <div className="flex justify-between items-start mb-2">
                                                <div className="flex items-center gap-2">
                                                    <span className="bg-blue-100 font-medium text-xs text-blue-700 rounded-full px-2 py-0">
                                                        {projection.author}
                                                    </span>
                                                </div>
                                                <span className="text-xs text-gray-500">
                                                    {projection.date} at {projection.time}
                                                </span>
                                            </div>

                                            {editingProjectionId === projection.id ? (
                                                // Modo edición
                                                <div className="space-y-2">
                                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                                                        <Select
                                                            placeholder="Select Projection Type"
                                                            selectedKeys={editingProjection.projection_id ? [editingProjection.projection_id.toString()] : []}
                                                            onSelectionChange={(keys) => {
                                                                const selected = Array.from(keys)[0];
                                                                if (selected) {
                                                                    const selectedProj = availableProjections.find(p => p.id === parseInt(selected));
                                                                    setEditingProjection(prev => ({
                                                                        ...prev,
                                                                        projection_id: parseInt(selected),
                                                                        title: selectedProj?.title || prev.title
                                                                    }));
                                                                } else {
                                                                    setEditingProjection(prev => ({
                                                                        ...prev,
                                                                        projection_id: null
                                                                    }));
                                                                }
                                                            }}
                                                            size="sm"
                                                            classNames={{
                                                                trigger: "h-8 min-h-8",
                                                                value: "text-sm"
                                                            }}
                                                        >
                                                            {availableProjections.map((proj) => (
                                                                <SelectItem key={proj.id.toString()} value={proj.id.toString()}>
                                                                    {proj.title}
                                                                </SelectItem>
                                                            ))}
                                                        </Select>
                                                        <Input
                                                            placeholder="Título"
                                                            value={editingProjection.title}
                                                            onChange={(e) => setEditingProjection(prev => ({ ...prev, title: e.target.value }))}
                                                        />
                                                        <Input
                                                            placeholder="QuickView %"
                                                            value={editingProjection.quickview_percent}
                                                            onChange={(e) => {
                                                                const percentStr = e.target.value;
                                                                const percentNum = parseFloat(percentStr);
                                                                const reclamoUsd = parseFloat(formData.reclamo_usd);

                                                                setEditingProjection(prev => {
                                                                    const newState = { ...prev, quickview_percent: percentStr };
                                                                    if (!isNaN(percentNum) && percentNum >= 0 && !isNaN(reclamoUsd) && reclamoUsd >= 0) {
                                                                        const calc = (reclamoUsd * percentNum) / 100;
                                                                        newState.proyeccion_usd = calc.toFixed(2);
                                                                        if (percentNum === 0) {
                                                                            newState.fecha_estimada = "";
                                                                        }
                                                                    } else {
                                                                        newState.proyeccion_usd = "";
                                                                    }
                                                                    return newState;
                                                                });
                                                            }}
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            max="100"
                                                            isDisabled={parseFloat(editingProjection.proyeccion_usd) === 0}
                                                        />
                                                        <Input
                                                            placeholder="Proyección USD"
                                                            value={editingProjection.proyeccion_usd}
                                                            onChange={(e) => {
                                                                const value = e.target.value;
                                                                setEditingProjection(prev => {
                                                                    const newState = { ...prev, proyeccion_usd: value };

                                                                    // Si proyección USD = 0, limpiar otros campos
                                                                    if (parseFloat(value) === 0) {
                                                                        newState.quickview_percent = "";
                                                                        newState.fecha_estimada = "";
                                                                    }

                                                                    return newState;
                                                                });
                                                            }}
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                        />
                                                        <YearQuarterInput
                                                            label="Fecha estimada de recupero"
                                                            value={editingProjection.fecha_estimada}
                                                            onChange={(value) => setEditingProjection(prev => ({ ...prev, fecha_estimada: value }))}
                                                            isReadOnly={parseFloat(editingProjection.proyeccion_usd) === 0}
                                                            icon="lucide:calendar"
                                                        />
                                                    </div>
                                                    <div className="flex gap-2">
                                                        <Button
                                                            size="sm"
                                                            color="primary"
                                                            onPress={() => handleSaveEditProjection(projection.id)}
                                                            isDisabled={
                                                                !editingProjection.projection_id ||
                                                                !editingProjection.title.trim() ||
                                                                (parseFloat(editingProjection.proyeccion_usd) > 0 && (!editingProjection.quickview_percent || !editingProjection.fecha_estimada))
                                                            }
                                                        >
                                                            Save
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            color="default"
                                                            variant="light"
                                                            onPress={handleCancelEditProjection}
                                                        >
                                                            Cancel
                                                        </Button>
                                                    </div>
                                                </div>
                                            ) : (
                                                // Modo visualización
                                                <div className="space-y-3">
                                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                                                        <div className="space-y-1">
                                                            <label className="text-xs text-gray-700 font-medium flex items-center justify-center gap-1">
                                                                Título
                                                                <Icon icon="lucide:hash" className="text-gray-600" width={12} height={12} />
                                                            </label>
                                                            <Input
                                                                value={projection.title}
                                                                isReadOnly={true}
                                                                size="sm"
                                                                classNames={{
                                                                    input: "text-center text-sm font-semibold text-gray-800",
                                                                    inputWrapper: "bg-white border-gray-300 h-8"
                                                                }}
                                                            />
                                                        </div>

                                                        <div className="space-y-1">
                                                            <label className="text-xs text-gray-700 font-medium flex items-center justify-center gap-1">
                                                                % Proyectado
                                                                <Icon icon="lucide:percent" className="text-gray-600" width={12} height={12} />
                                                            </label>
                                                            <Input
                                                                value={formatPercentDisplay(projection.quickview_percent)}
                                                                isReadOnly={true}
                                                                size="sm"
                                                                classNames={{
                                                                    input: "text-center text-sm font-semibold text-gray-800",
                                                                    inputWrapper: "bg-white border-gray-300 h-8"
                                                                }}
                                                            />
                                                        </div>

                                                        <div className="space-y-1">
                                                            <label className="text-xs text-gray-700 font-medium flex items-center justify-center gap-1">
                                                                Proyección USD
                                                                <Icon icon="lucide:dollar-sign" className="text-gray-600" width={12} height={12} />
                                                            </label>
                                                            <Input
                                                                value={`$${new Intl.NumberFormat('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
                                                                    (projection.proyeccion_usd ?? ((projection.quickview_percent || 0) * (maestraData?.reclamo_usd || 0) / 100))
                                                                )}`}
                                                                isReadOnly={true}
                                                                size="sm"
                                                                classNames={{
                                                                    input: "text-center text-sm font-semibold text-gray-800",
                                                                    inputWrapper: "bg-white border-gray-300 h-8"
                                                                }}
                                                            />
                                                        </div>

                                                        <div className="space-y-1">
                                                            <label className="text-xs text-gray-700 font-medium flex items-center justify-center gap-1">
                                                                Fecha estimada de recupero
                                                                <Icon icon="lucide:calendar" className="text-gray-600" width={12} height={12} />
                                                            </label>
                                                            <Input
                                                                value={projection.fecha_estimada || "N/A"}
                                                                isReadOnly={true}
                                                                size="sm"
                                                                classNames={{
                                                                    input: "text-center text-sm font-semibold text-gray-800",
                                                                    inputWrapper: "bg-white border-gray-300 h-8"
                                                                }}
                                                            />
                                                        </div>
                                                    </div>

                                                    {canUserModifyProjection(projection) && isEditing && (
                                                        <div className="flex gap-2 pt-2">
                                                            <Button
                                                                size="sm"
                                                                color="default"
                                                                variant="light"
                                                                onPress={() => handleStartEditProjection(projection)}
                                                                startContent={<Icon icon="lucide:edit" />}
                                                            >
                                                                Edit
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                color="danger"
                                                                variant="light"
                                                                onPress={() => handleDeleteProjection(projection.id)}
                                                                startContent={<Icon icon="lucide:trash-2" />}
                                                            >
                                                                Delete
                                                            </Button>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </CardBody>
                </Card>
            </div>

            <Spacer />

            <div className="max-w-full">
                <Card className="w-full">
                    <CardBody>
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-2xl font-bold">Comments</h2>
                        </div>
                        <Divider className="my-4" />

                        {/* Comentario de QuickView - SOLO LECTURA */}
                        {comments.find(c => c.isQuickViewComment) && (
                            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full">
                                        QuickView
                                    </span>
                                    <span className="text-xs text-gray-500 ml-auto">
                                        {comments.find(c => c.isQuickViewComment)?.date} at {comments.find(c => c.isQuickViewComment)?.time}
                                    </span>
                                </div>
                                <p className="text-green-800 whitespace-pre-wrap text-sm">
                                    {comments.find(c => c.isQuickViewComment)?.text}
                                </p>
                            </div>
                        )}

                        {/* Campo para agregar nuevo comentario */}
                        <div className="flex gap-2 mb-4">
                            <Input
                                placeholder={loadingProfile ? "Loading user profile..." : (isEditing ? "Write a new comment..." : "Enable Edit to add comments")}
                                value={newComment}
                                onChange={(e) => setNewComment(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && isEditing && handleAddComment()}
                                className="flex-1"
                                isDisabled={loadingProfile || !isEditing}
                                startContent={
                                    <Icon
                                        icon="lucide:message-circle"
                                        className="text-default-400 pointer-events-none flex-shrink-0"
                                    />
                                }
                            />
                            <Button
                                color="primary"
                                onPress={handleAddComment}
                                isDisabled={!newComment.trim() || loadingProfile || !isEditing}
                                startContent={<Icon icon="lucide:plus" />}
                            >
                                Add
                            </Button>
                        </div>



                        {/* Lista de comentarios del usuario */}
                        <div className="space-y-3">
                            {comments.filter(comment => !comment.isQuickViewComment && !comment.isAIComment).length === 0 ? (
                                <div className="flex items-center justify-center gap-2 py-0 text-gray-500">
                                    <Icon icon="lucide:message-circle" className="text-2xl text-gray-300" />
                                    <p className="m-0">No user comments yet. Add the first comment above.</p>
                                </div>
                            ) : (
                                comments.filter(comment => !comment.isQuickViewComment && !comment.isAIComment).map((comment) => (
                                    <div
                                        key={comment.id}
                                        className="border border-gray-200 rounded-lg p-3 bg-gray-50"
                                    >
                                        <div className="flex justify-between items-start mb-2">
                                            <div className="flex items-center gap-2">
                                                <span className="bg-blue-100 font-medium text-xs text-blue-700 rounded-full px-2 py-0">
                                                    {comment.author}
                                                </span>
                                            </div>
                                            <span className="text-xs text-gray-500">
                                                {comment.date} at {comment.time}
                                            </span>
                                        </div>

                                        {editingCommentId === comment.id ? (
                                            // Modo edición
                                            <div className="space-y-2">
                                                <Textarea
                                                    value={editingText}
                                                    onChange={(e) => setEditingText(e.target.value)}
                                                    className="w-full"
                                                    rows={2}
                                                />
                                                <div className="flex gap-2">
                                                    <Button
                                                        size="sm"
                                                        color="success"
                                                        onPress={() => handleSaveEdit(comment.id)}
                                                        isDisabled={!editingText.trim()}
                                                        startContent={<Icon icon="lucide:check" />}
                                                    >
                                                        Save
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        color="default"
                                                        variant="light"
                                                        onPress={handleCancelEdit}
                                                        startContent={<Icon icon="lucide:x" />}
                                                    >
                                                        Cancel
                                                    </Button>
                                                </div>
                                            </div>
                                        ) : (
                                            // Modo visualización
                                            <div className="flex justify-between items-start">
                                                <p className="text-gray-800 text-sm whitespace-pre-wrap flex-1">
                                                    {comment.text}
                                                </p>
                                                {canUserModifyComment(comment) && isEditing && (
                                                    <div className="flex gap-2 ml-2">
                                                        <Button
                                                            size="sm"
                                                            color="primary"
                                                            variant="light"
                                                            onPress={() => handleStartEdit(comment)}
                                                            startContent={<Icon icon="lucide:edit" />}
                                                        >
                                                            Edit
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            color="danger"
                                                            variant="light"
                                                            onPress={() => handleDeleteComment(comment.id)}
                                                            startContent={<Icon icon="lucide:trash-2" />}
                                                        >
                                                            Delete
                                                        </Button>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>

                        {/* Comentarios de IA - Por defecto solo el más reciente; botón para mostrar todos */}
                        {(() => {
                            const aiComments = comments
                                .filter(comment => comment.isAIComment)
                                .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
                            const visibleAIComments = showAllAIComments ? aiComments : aiComments.slice(0, 1);
                            const hasMoreAI = aiComments.length > 1;
                            if (aiComments.length === 0) return null;
                            return (
                                <>
                                    <Divider className="my-4" />
                                    <div className="flex items-center justify-between gap-2 mb-3">
                                        <h3 className="text-lg font-semibold">AI Comments</h3>
                                        {hasMoreAI && (
                                            <Button
                                                size="sm"
                                                variant="flat"
                                                color="primary"
                                                onPress={() => setShowAllAIComments(prev => !prev)}
                                                startContent={<Icon icon={showAllAIComments ? "lucide:chevron-up" : "lucide:chevron-down"} />}
                                            >
                                                {showAllAIComments ? "Show less" : `Show all (${aiComments.length})`}
                                            </Button>
                                        )}
                                    </div>
                                    <div className="space-y-3">
                                        {visibleAIComments.map((comment) => (
                                            <div
                                                key={comment.id}
                                                className="border border-blue-200 rounded-lg p-3 bg-blue-50"
                                            >
                                                <div className="flex flex-col gap-1 mb-2">
                                                    <span className="text-xs text-gray-500">
                                                        {comment.date} at {comment.time}
                                                    </span>
                                                    <div className="flex items-center gap-2">
                                                        <span className="bg-blue-100 font-medium text-xs text-blue-700 rounded-full px-2 py-0">
                                                            {comment.author}:
                                                        </span>
                                                    </div>
                                                </div>
                                                <p className="text-blue-700 text-sm whitespace-pre-wrap">
                                                    {comment.text}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            );
                        })()}
                    </CardBody>
                </Card>
            </div>

            <Spacer />

            <div className="max-w-full">
                <Card className="w-full">
                    <CardBody>
                        <h2 className="text-2xl font-bold">Other fields</h2>
                        <Divider className="my-4" />
                        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                            {otherFields.map((field, index) => renderField(field, index))}
                        </div>
                    </CardBody>
                </Card>
            </div>

            <Spacer />

            {!readOnlyClaims && (
                <div className="flex justify-between">
                    <Button
                        variant="ghost"
                        color="primary"
                        onPress={toggleEditMode}
                        startContent={<Icon icon={isEditing ? "lucide:lock" : "lucide:unlock"} />}
                    >
                        {isEditing ? "Lock edition" : "Edit"}
                    </Button>

                    <Button color="primary" onPress={handleSave} isDisabled={!isEditing}>
                        Save
                    </Button>
                </div>
            )}
        </div>
    );
}

export default DetalleMaestraEditar;
